/**
 * EarningsHistory Component
 *
 * A visually appealing, user-friendly earnings history display
 * that shows platform payouts, monetization revenue, and sales earnings.
 *
 * Features:
 * - Real-time earnings updates (future)
 * - Responsive design (mobile-first)
 * - Pagination with configurable page sizes
 * - Type-coded and status-coded badges
 * - Color-coded amount indicators
 * - Smooth animations for new earnings
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import styled, { keyframes, css } from 'styled-components';
import type { Earning } from '../../types/earnings';
import { useAuth } from '../../context/AuthContext';
import { useMaybeWalletEarnings } from '../../context/WalletEarningsContext';
import { useMaybeOLVRate } from '../../context/OLVRateContext';
import { CopyableAmount } from '../common/CopyableAmount';
import { CopyableId, CopyableMember, CopyableSystem, CopyableStatus, InlineCopyableText, CopyableTags } from '../common/CopyableValue';
import { getCurrencyLabel } from '../common/CopyableAmount/utils';
import { usdToWu, formatWu } from '../../config/workUnits';
import {
  type SummaryPeriod,
  filterByPeriod,
  findSmallestPeriodWithData,
  getPeriodDataAvailability
} from '../../utils/timePeriodHelpers';

// ============================================================================
// Animations
// ============================================================================

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

// ============================================================================
// Styled Components
// ============================================================================

const Container = styled.div`
  background: white;
  border-radius: 1rem;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 0.5rem 2rem rgba(0, 0, 0, 0.08);
  border: 1px solid rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  overflow: visible;

  @media (min-width: 768px) {
    padding: 2rem;
    margin-bottom: 2rem;
    border-radius: 1.25rem;
  }

  &:hover {
    box-shadow: 0 0.75rem 2.5rem rgba(0, 0, 0, 0.1);
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const Title = styled.h2`
  font-family: 'Space Grotesk', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 1.25rem;
  color: #1a1a2e;
  margin: 0;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-transform: lowercase;

  @media (min-width: 768px) {
    font-size: 1.5rem;
  }
`;

const TitleIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  background: linear-gradient(135deg, var(--brand-gold) 0%, var(--brand-gold-hover) 100%);
  border-radius: 0.5rem;
  color: white;
  font-size: 1rem;

  @media (min-width: 768px) {
    width: 2.25rem;
    height: 2.25rem;
    font-size: 1.125rem;
  }
`;

const Controls = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
`;

const ControlGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  color: var(--brand-text-secondary);
  text-transform: lowercase;
`;

const Select = styled.select`
  padding: 0.375rem 0.75rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.5rem;
  background: white;
  font-size: 0.85rem;
  color: #334155;
  cursor: pointer;
  text-transform: lowercase;
  transition: all 0.2s;

  &:hover {
    border-color: #94a3b8;
  }

  &:focus {
    outline: none;
    border-color: var(--brand-gold);
    box-shadow: 0 0 0 3px rgba(198, 167, 94, 0.1);
  }
`;

const Pagination = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const PageButton = styled.button<{ disabled?: boolean }>`
  padding: 0.375rem 0.75rem;
  border: 1px solid ${props => props.disabled ? '#e2e8f0' : 'var(--brand-gold)'};
  border-radius: 0.5rem;
  background: ${props => props.disabled ? '#f8fafc' : 'white'};
  color: ${props => props.disabled ? '#94a3b8' : 'var(--brand-gold)'};
  font-size: 0.85rem;
  font-weight: 500;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  text-transform: lowercase;
  transition: all 0.2s;

  &:hover:not(:disabled) {
    background: var(--brand-gold);
    color: white;
  }
`;

const PageInfo = styled.span`
  font-size: 0.85rem;
  color: var(--brand-text-secondary);
  padding: 0 0.5rem;
  text-transform: lowercase;
`;

const TableWrapper = styled.div`
  width: 100%;
  margin-top: 1rem;
  border-radius: 0.75rem;
  border: 1px solid #e2e8f0;
  background: white;
  position: relative;
  overflow: visible;
`;

const TableScrollContainer = styled.div`
  width: 100%;
  overflow-x: auto;
  overflow-y: visible;
  border-radius: 0.75rem;
  padding-top: 0.25rem;

  &::-webkit-scrollbar {
    height: 6px;
  }
  &::-webkit-scrollbar-track {
    background: #f1f5f9;
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb {
    background: #cbd5e1;
    border-radius: 3px;
  }
  &::-webkit-scrollbar-thumb:hover {
    background: #94a3b8;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  overflow: visible;
  min-width: 600px;

  @media (min-width: 768px) {
    min-width: 680px;
  }

  @media (min-width: 1024px) {
    min-width: 760px;
  }

  @media (min-width: 1200px) {
    min-width: 840px;
  }

  thead, tbody {
    overflow: visible;
  }
`;

const Th = styled.th`
  text-align: left;
  padding: 0.25rem 0.75rem;
  border-bottom: 2px solid #e2e8f0;
  color: #475569;
  font-weight: 600;
  font-size: 0.7rem;
  text-transform: lowercase;
  white-space: nowrap;
  position: sticky;
  top: 0;
  background: white;
  z-index: 10;

  /* ===========================================
   * MOBILE-FIRST: Base styles (< 768px)
   * 8 columns: date/time, tags, summary, id, member, amount, system, status
   * Total: 12% + 8% + 27.5% + 10.5% + 10.5% + 10.5% + 10.5% + 10.5% = 100%
   * =========================================== */
  &:nth-child(1) { width: 12%; }  /* date / time */
  &:nth-child(2),
  &:nth-child(4),
  &:nth-child(5),
  &:nth-child(6),
  &:nth-child(7),
  &:nth-child(8) {
    text-align: center;
  }
  &:nth-child(2) { width: 8%; }      /* tags */
  &:nth-child(3) { width: 27.5%; }   /* summary */
  &:nth-child(4) { width: 10.5%; }   /* id */
  &:nth-child(5) {
    width: 10.5%;        /* member */
    padding-left: 0.9rem;  /* shift header slightly left */
  }
  &:nth-child(6) { width: 10.5%; }   /* amount */
  &:nth-child(7) { width: 10.5%; }   /* system */
  &:nth-child(8) { width: 10.5%; }   /* status */

  /* ===========================================
   * TABLET: 768px - 1023px
   * 8 columns with adjusted widths
   * Total: 11% + 7% + 32% + 10% + 10% + 10% + 10% + 10% = 100%
   * =========================================== */
  @media (min-width: 768px) {
    padding: 0.3rem 0.875rem;
    font-size: 0.75rem;

    &:nth-child(1) { width: 11%; }   /* date */
    &:nth-child(2) { width: 7%; }    /* tags */
    &:nth-child(3) { width: 32%; }   /* summary */
    &:nth-child(4) { width: 10%; }   /* id */
    &:nth-child(5) { width: 10%; }   /* member */
    &:nth-child(6) { width: 10%; }   /* amount */
    &:nth-child(7) { width: 10%; }   /* system */
    &:nth-child(8) { width: 10%; }   /* status */
  }

  /* ===========================================
   * LAPTOP: 1024px - 1199px
   * 8 columns with adjusted widths
   * Total: 11% + 7% + 32% + 10% + 10% + 10% + 10% + 10% = 100%
   * =========================================== */
  @media (min-width: 1024px) {
    padding: 0.35rem 1rem;
    font-size: 0.8rem;

    &:nth-child(1) { width: 11%; }   /* date */
    &:nth-child(2) { width: 7%; }    /* tags */
    &:nth-child(3) { width: 32%; }   /* summary */
    &:nth-child(4) { width: 10%; }   /* id */
    &:nth-child(5) { width: 10%; }   /* member */
    &:nth-child(6) { width: 10%; }   /* amount */
    &:nth-child(7) { width: 10%; }   /* system */
    &:nth-child(8) { width: 10%; }   /* status */
  }

  /* ===========================================
   * DESKTOP: 1200px+
   * 8 columns with adjusted widths
   * Total: 11% + 7% + 32% + 10% + 10% + 10% + 10% + 10% = 100%
   * =========================================== */
  @media (min-width: 1200px) {
    padding: 0.4rem 1.25rem;
    font-size: 0.85rem;

    &:nth-child(1) { width: 11%; }   /* date */
    &:nth-child(2) { width: 7%; }    /* tags */
    &:nth-child(3) { width: 32%; }   /* summary */
    &:nth-child(4) { width: 10%; }   /* id */
    &:nth-child(5) { width: 10%; }   /* member */
    &:nth-child(6) { width: 10%; }   /* amount */
    &:nth-child(7) { width: 10%; }   /* system */
    &:nth-child(8) { width: 10%; }   /* status */
  }
`;

const Tr = styled.tr<{ $isNew?: boolean }>`
  transition: background-color 0.2s;
  overflow: visible;

  ${props => props.$isNew && css`
    animation: ${fadeIn} 0.5s ease-out;
    background-color: rgba(198, 167, 94, 0.05);
  `}

  &:hover {
    background-color: #f8fafc;
  }

  &:nth-child(even) {
    background-color: rgba(248, 250, 252, 0.5);
  }

  &:nth-child(even):hover {
    background-color: #f1f5f9;
  }
`;

const Td = styled.td`
  padding: 0.25rem 0.75rem;
  border-bottom: 1px solid #f1f5f9;
  color: #334155;
  font-size: 0.8rem;
  vertical-align: middle;
  overflow: visible;
  position: relative;

  /* Center the icon columns (tags, id, member, amount, system, status) */
  &:nth-child(2),
  &:nth-child(4),
  &:nth-child(5),
  &:nth-child(6),
  &:nth-child(7),
  &:nth-child(8) {
    text-align: center;
  }

  /* ===========================================
   * TABLET: 768px+
   * =========================================== */
  @media (min-width: 768px) {
    padding: 0.3rem 0.875rem;
    font-size: 0.85rem;
  }

  /* ===========================================
   * LAPTOP: 1024px+
   * =========================================== */
  @media (min-width: 1024px) {
    padding: 0.35rem 1rem;
    font-size: 0.9rem;
  }

  /* ===========================================
   * DESKTOP: 1200px+
   * =========================================== */
  @media (min-width: 1200px) {
    padding: 0.4rem 1.25rem;
    font-size: 0.95rem;
  }
`;

const DateCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.1rem;

  @media (min-width: 768px) {
    gap: 0.125rem;
  }
`;

const DateValue = styled.span`
  font-weight: 600;
  font-size: 0.75rem;
  color: #1e293b;
  letter-spacing: -0.01em;

  @media (min-width: 768px) {
    font-size: 0.8rem;
  }

  @media (min-width: 1024px) {
    font-size: 0.85rem;
  }
`;

const TimeValue = styled.span`
  font-size: 0.625rem;
  color: #94a3b8;
  font-weight: 500;
  letter-spacing: 0.01em;

  @media (min-width: 768px) {
    font-size: 0.675rem;
  }

  @media (min-width: 1024px) {
    font-size: 0.7rem;
  }
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
  text-align: center;
`;

const EmptyIcon = styled.div`
  width: 4rem;
  height: 4rem;
  border-radius: 1rem;
  background: linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  margin-bottom: 1rem;
  color: #94a3b8;
`;

const EmptyTitle = styled.h3`
  font-family: 'Space Grotesk', 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 1.125rem;
  color: #475569;
  margin: 0 0 0.5rem 0;
  font-weight: 500;
  text-transform: lowercase;
`;

const EmptyDescription = styled.p`
  font-size: 0.9rem;
  color: #94a3b8;
  margin: 0;
  max-width: 300px;
  text-transform: lowercase;
`;

const SummarySection = styled.div`
  background: linear-gradient(135deg, #FDFBF5 0%, #F5F0E1 100%);
  border-radius: 0.75rem;
  margin-bottom: 1rem;
  overflow: hidden;
`;

const PeriodSelector = styled.div`
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid rgba(198, 167, 94, 0.1);

  @media (min-width: 768px) {
    padding: 0.75rem 1.5rem;
  }
`;

const PeriodButton = styled.button<{ $active: boolean; $disabled?: boolean }>`
  padding: 0.375rem 0.75rem;
  border: none;
  border-radius: 0.5rem;
  background: ${props => props.$active ? 'white' : 'transparent'};
  color: ${props => props.$disabled ? '#9ca3af' : props.$active ? 'var(--brand-gold-hover)' : 'var(--brand-text-secondary)'};
  font-size: 0.8rem;
  font-weight: ${props => props.$active ? '600' : '500'};
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.$disabled ? 0.5 : 1};
  transition: all 0.2s ease;
  text-transform: lowercase;
  box-shadow: ${props => props.$active ? '0 1px 3px rgba(0, 0, 0, 0.1)' : 'none'};

  &:hover {
    background: ${props => props.$disabled ? 'transparent' : props.$active ? 'white' : 'rgba(255, 255, 255, 0.5)'};
    color: ${props => props.$disabled ? '#9ca3af' : 'var(--brand-gold-hover)'};
  }
`;

const SummaryBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem;
  flex-wrap: wrap;
  gap: 1rem;

  @media (min-width: 768px) {
    padding: 1rem 1.5rem;
  }
`;

const SummaryItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
`;

const SummaryLabel = styled.span`
  font-family: Arial, Helvetica, sans-serif !important;  /* Windows-safe fonts only */
  font-size: 0.7rem;  /* Increased to prevent Windows rendering issues */
  color: var(--brand-gold-hover);
  font-weight: 500;
  text-transform: none !important;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
`;

const SummaryValue = styled.span`
  font-size: 1.1rem;
  font-weight: 700;
  color: #8B7333;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.01em;

  @media (min-width: 768px) {
    font-size: 1.25rem;
  }
`;

const LoadingOverlay = styled.div`
  position: relative;
  width: 100%;
  height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(90deg, #f1f5f9 0%, #e2e8f0 50%, #f1f5f9 100%);
    background-size: 200% 100%;
    animation: ${shimmer} 1.5s infinite;
    border-radius: 0.5rem;
  }
`;

const AutoLoadIndicator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  background: linear-gradient(90deg, #f1f5f9 0%, #e2e8f0 50%, #f1f5f9 100%);
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s infinite;
  border-radius: 0.5rem;
  margin: 0.5rem 0;
  gap: 0.75rem;
`;

const AutoLoadText = styled.div`
  font-size: 0.875rem;
  color: var(--brand-text-secondary);
  font-weight: 500;
`;

const AutoLoadSpinner = styled.div`
  width: 16px;
  height: 16px;
  border: 2px solid #e2e8f0;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const AutoLoadComplete = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.75rem;
  background: #FDFBF5;
  border: 1px solid #E8DFC4;
  border-radius: 0.5rem;
  margin: 0.5rem 0;
  gap: 0.5rem;
  color: var(--brand-gold-hover);
  font-size: 0.875rem;
  font-weight: 500;
`;

// ============================================================================
// Helper Functions
// ============================================================================

function formatDate(timestamp: number): { date: string; time: string } {
  const d = new Date(timestamp);
  return {
    date: d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
    time: d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
  };
}

function formatAmount(amount: number): string {
  if (amount === 0) return '0.00';
  // Very small amounts: show up to 8 decimal places
  if (amount < 0.01) {
    return amount.toFixed(8).replace(/0+$/, '').replace(/\.$/, '.00');
  }
  // Small amounts: show up to 6 decimal places
  if (amount < 1) {
    return amount.toFixed(6).replace(/0+$/, '').replace(/\.$/, '.00');
  }
  // Medium amounts: show up to 4 decimal places
  if (amount < 100) {
    return amount.toFixed(4).replace(/0+$/, '').replace(/\.$/, '.00');
  }
  // Large amounts: show up to 4 decimal places with locale formatting
  return amount.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  });
}

/**
 * Best-available USD for a ledger row (2026-08 PRO Work Units migration —
 * display only): prefer the stored USD snapshot, else reconstruct from the
 * OLV snapshot at the row's historical rate, else fall back to the current
 * live rate. WU = rowUsd / 2.18 (Standard Rate) everywhere it renders.
 */
function deriveRowUsd(
  row: { amountUsd: number; amountOlv: number; olvRateAtTime?: number },
  currentRate: number
): number {
  if (row.amountUsd > 0) return row.amountUsd;
  if (row.amountOlv > 0 && (row.olvRateAtTime ?? 0) > 0) {
    return row.amountOlv * (row.olvRateAtTime as number);
  }
  return row.amountOlv > 0 && currentRate > 0 ? row.amountOlv * currentRate : row.amountUsd;
}

// ============================================================================
// Component
// ============================================================================

interface EarningsHistoryProps {
  /** Maximum earnings to show (default: unlimited, uses pagination) */
  maxEarnings?: number;
  /** Show the summary bar (default: true) */
  showSummary?: boolean;
  /** Compact mode for embedding in smaller spaces */
  compact?: boolean;
  /** Additional className */
  className?: string;
}

export const EarningsHistory: React.FC<EarningsHistoryProps> = ({
  maxEarnings,
  showSummary = true,
  compact = false,
  className,
}) => {
  // Get earnings from Firestore context (with real-time sync)
  const walletEarnings = useMaybeWalletEarnings();
  const isLoading = walletEarnings?.state.isLoading ?? true;
  const earnings = useMemo(() => {
    // Use context data if available, already sorted by timestamp DESC
    return walletEarnings?.state.earnings ?? [];
  }, [walletEarnings?.state.earnings]);

  // Get current user for member lookup
  const { user } = useAuth();

  // Live rate — LAST-RESORT fallback only for legacy rows missing both the
  // USD snapshot and the historical rate (see deriveRowUsd).
  const olvRate = useMaybeOLVRate();
  const currentRate = olvRate?.getEffectiveRate() ?? 0;

  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [summaryPeriod, setSummaryPeriod] = useState<SummaryPeriod>('day');
  const [initialPeriodSet, setInitialPeriodSet] = useState(false);
  const [isAutoLoading, setIsAutoLoading] = useState(false);
  const [autoLoadProgress, setAutoLoadProgress] = useState<{ loaded: number; hasMore: boolean } | null>(null);

  // Auto-detect best period on mount when data loads
  useEffect(() => {
    if (!initialPeriodSet && !isLoading && earnings.length > 0) {
      const bestPeriod = findSmallestPeriodWithData(earnings);
      setSummaryPeriod(bestPeriod);
      setInitialPeriodSet(true);
    }
  }, [earnings, isLoading, initialPeriodSet]);

  // Auto-load all records when "all" period is selected
  useEffect(() => {
    // Only auto-load when "all" period is selected
    if (summaryPeriod !== 'all') {
      // Clear auto-load state when switching away from "all"
      setIsAutoLoading(false);
      setAutoLoadProgress(null);
      return;
    }

    // Only if there are more records to load
    if (!walletEarnings?.state.hasMore) {
      // No more records - mark as complete
      if (isAutoLoading) {
        setIsAutoLoading(false);
        setAutoLoadProgress(null);
      }
      return;
    }

    // Prevent infinite loops - only load if not currently loading
    if (walletEarnings?.state.isLoading) return;

    // Mark as auto-loading
    if (!isAutoLoading) {
      setIsAutoLoading(true);
    }

    // Update progress
    setAutoLoadProgress({
      loaded: earnings.length,
      hasMore: walletEarnings.state.hasMore,
    });

    // Auto-load more records
    const autoLoad = async () => {
      try {
        await walletEarnings.loadMore();
      } catch (error) {
        console.error('[EarningsHistory] Auto-load failed:', error);
        setIsAutoLoading(false);
        setAutoLoadProgress(null);
      }
    };

    autoLoad();
  }, [
    summaryPeriod,
    walletEarnings?.state.hasMore,
    walletEarnings?.state.isLoading,
    walletEarnings,
    earnings.length,
    isAutoLoading
  ]);

  // Filter earnings by time period FIRST
  const filteredEarnings = useMemo(() => {
    const baseEarnings = maxEarnings ? earnings.slice(0, maxEarnings) : earnings;
    return filterByPeriod(baseEarnings, summaryPeriod);
  }, [earnings, maxEarnings, summaryPeriod]);

  // Calculate which periods have data (for disabling empty period buttons)
  const periodAvailability = useMemo(() => {
    return getPeriodDataAvailability(earnings);
  }, [earnings]);

  // Calculate paginated data from FILTERED earnings
  const totalEarnings = filteredEarnings.length;
  const totalPages = Math.max(1, Math.ceil(totalEarnings / pageSize));
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, totalEarnings);
  const pageEarnings = filteredEarnings.slice(startIndex, endIndex);

  // Calculate summary from ALL filtered items.
  // R18 (2026-08-09): earnings ABSORBED by a trial credit (the $100 welcome
  // seed a redeemed access code replaces) are excluded from totals — counting
  // them showed "+$100" standing next to the $1,000 tier credit that actually
  // replaced it. The rows still render (marked "replaced by trial credit").
  const summary = useMemo(() => {
    const counted = filteredEarnings.filter((e) => e.absorbedIntoTrial !== true);
    const totalUsd = counted.reduce((sum, e) => sum + e.amountUsd, 0);
    // WU total: derive each row's best-available USD, then convert at the
    // Standard Rate — matches the per-row WU figures below.
    const totalWu = counted.reduce(
      (sum, e) => sum + usdToWu(deriveRowUsd(e, currentRate)),
      0
    );
    return {
      totalEarnings: counted.length,
      totalUsd,
      totalWu,
    };
  }, [filteredEarnings, currentRate]);

  // Reset to page 1 when page size changes
  const handlePageSizeChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setPageSize(parseInt(e.target.value, 10));
    setCurrentPage(1);
  }, []);

  // Handle period change with guard for empty periods
  const handlePeriodChange = useCallback((period: SummaryPeriod) => {
    // Don't allow selecting periods with no data
    if (!getPeriodDataAvailability(earnings)[period]) {
      return;
    }
    setSummaryPeriod(period);
    setCurrentPage(1);
  }, [earnings]);

  if (isLoading) {
    return (
      <Container className={className}>
        <Header>
          <Title>
            <TitleIcon>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                <line x1="12" y1="1" x2="12" y2="23" />
                <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
              </svg>
            </TitleIcon>
            earnings
          </Title>
        </Header>
        <LoadingOverlay />
      </Container>
    );
  }

  return (
    <Container className={className}>
      <Header>
        <Title>
          <TitleIcon>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <line x1="12" y1="1" x2="12" y2="23" />
              <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
            </svg>
          </TitleIcon>
          earnings
        </Title>

        {!compact && totalEarnings > 0 && (
          <Controls>
            <ControlGroup>
              <span>show</span>
              <Select value={pageSize} onChange={handlePageSizeChange}>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </Select>
            </ControlGroup>

            {totalPages > 1 && (
              <Pagination>
                <PageButton
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage <= 1}
                >
                  prev
                </PageButton>
                <PageInfo>
                  {currentPage} / {totalPages}
                </PageInfo>
                <PageButton
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage >= totalPages}
                >
                  next
                </PageButton>
              </Pagination>
            )}
          </Controls>
        )}
      </Header>

      {showSummary && totalEarnings > 0 && (
        <SummarySection>
          <PeriodSelector>
            <PeriodButton
              $active={summaryPeriod === 'day'}
              $disabled={!periodAvailability.day}
              onClick={() => handlePeriodChange('day')}
              title={!periodAvailability.day ? 'No data in this period' : undefined}
            >
              24h
            </PeriodButton>
            <PeriodButton
              $active={summaryPeriod === 'week'}
              $disabled={!periodAvailability.week}
              onClick={() => handlePeriodChange('week')}
              title={!periodAvailability.week ? 'No data in this period' : undefined}
            >
              7d
            </PeriodButton>
            <PeriodButton
              $active={summaryPeriod === 'month'}
              $disabled={!periodAvailability.month}
              onClick={() => handlePeriodChange('month')}
              title={!periodAvailability.month ? 'No data in this period' : undefined}
            >
              30d
            </PeriodButton>
            <PeriodButton
              $active={summaryPeriod === 'all'}
              $disabled={!periodAvailability.all}
              onClick={() => handlePeriodChange('all')}
              title={!periodAvailability.all ? 'No data available' : undefined}
            >
              all
            </PeriodButton>
          </PeriodSelector>
          <SummaryBar>
            <SummaryItem>
              <SummaryLabel>earnings</SummaryLabel>
              <SummaryValue>{summary.totalEarnings.toLocaleString()}</SummaryValue>
            </SummaryItem>
            <SummaryItem>
              <SummaryLabel>total {getCurrencyLabel('usd')}</SummaryLabel>
              <SummaryValue>+${formatAmount(summary.totalUsd)}</SummaryValue>
            </SummaryItem>
            <SummaryItem>
              <SummaryLabel>total {getCurrencyLabel('wu')}</SummaryLabel>
              <SummaryValue>+{formatWu(summary.totalWu)}</SummaryValue>
            </SummaryItem>
          </SummaryBar>
        </SummarySection>
      )}

      {/* Show loading indicator and hide table during auto-load */}
      {isAutoLoading ? (
        <AutoLoadIndicator>
          <AutoLoadSpinner />
          <AutoLoadText>
            Loading earnings... ({autoLoadProgress?.loaded ?? 0}{autoLoadProgress?.hasMore ? '+' : ''} loaded)
          </AutoLoadText>
        </AutoLoadIndicator>
      ) : (
      (() => {
        // Check if there's ANY unfiltered data
        const hasAnyData = earnings.length > 0;

        // No data at all
        if (!hasAnyData) {
          return (
            <EmptyState>
              <EmptyIcon>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="1" x2="12" y2="23" />
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                </svg>
              </EmptyIcon>
              <EmptyTitle>no earnings yet</EmptyTitle>
              <EmptyDescription>
                platform payouts, monetization revenue, and sales from goods or services will appear here
              </EmptyDescription>
            </EmptyState>
          );
        }

        // Has data but filtered out
        if (totalEarnings === 0 && hasAnyData) {
          return (
            <EmptyState>
              <EmptyIcon>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="12" y1="1" x2="12" y2="23" />
                  <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                </svg>
              </EmptyIcon>
              <EmptyTitle>no earnings in this time period</EmptyTitle>
              <EmptyDescription>
                try selecting "all" to see all data.
              </EmptyDescription>
            </EmptyState>
          );
        }

        // Has filtered data - show table
        return (
        <TableWrapper>
          <TableScrollContainer>
            <Table>
              <thead>
                <tr>
                  <Th>date / time</Th>
                  <Th>tags</Th>
                  <Th>summary</Th>
                  <Th>id</Th>
                  <Th>member</Th>
                  <Th>amount</Th>
                  <Th>system</Th>
                  <Th>status</Th>
                </tr>
              </thead>
              <tbody>
                {pageEarnings.map((earning) => {
                  const { date, time } = formatDate(earning.timestamp);
                  const absorbed = earning.absorbedIntoTrial === true;
                  // Display-only WU conversion at the Standard Rate ($2.18/WU).
                  const rowUsd = deriveRowUsd(earning, currentRate);
                  const rowWu = usdToWu(rowUsd);

                  return (
                    <Tr key={earning.id} style={absorbed ? { opacity: 0.55 } : undefined}>
                      <Td>
                        <DateCell>
                          <DateValue>{date}</DateValue>
                          <TimeValue>{time}</TimeValue>
                        </DateCell>
                      </Td>
                      <Td>
                        <CopyableTags tags={earning.tags || []} />
                      </Td>
                      <Td>
                        <InlineCopyableText
                          text={
                            absorbed
                              ? `${earning.summary || earning.source} — replaced by trial credit`
                              : (earning.summary || earning.source)
                          }
                          emptyText="no summary"
                        />
                      </Td>
                      <Td>
                        <CopyableId
                          id={earning.id}
                          type="transaction"
                        />
                      </Td>
                      <Td>
                        <CopyableMember
                          memberId={earning.memberId || user?.profileId || user?.id}
                          fullName={user
                            ? (user.firstName && user.lastName
                                ? `${user.firstName} ${user.lastName}`.trim()
                                : user.name)
                            : undefined}
                          email={user?.email}
                        />
                      </Td>
                      <Td>
                        <CopyableAmount
                          olvValue={rowWu}
                          usdValue={rowUsd}
                          unitCurrency="wu"
                          showPositive={true}
                        />
                      </Td>
                      <Td>
                        <CopyableSystem
                          modelId={earning.modelId}
                          provider={earning.provider}
                        />
                      </Td>
                      <Td>
                        <CopyableStatus status={earning.status === 'completed' ? 'successful' : earning.status === 'processing' ? 'paused' : 'failed'} />
                      </Td>
                    </Tr>
                  );
                })}
              </tbody>
            </Table>
          </TableScrollContainer>
        </TableWrapper>
        );
      })()
      )}
    </Container>
  );
};

export default EarningsHistory;
